import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, Copy, MessageCircle, Sparkles, Banknote, Lock, Globe, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { PriceLock, hasPrice } from "@/components/PriceLock";
import { useAuth } from "@/contexts/AuthContext";
import { BANK_INFO } from "@/data/services";

export const ServiceDialog = ({ service, open, onOpenChange }) => {
  const { lang, t } = useLanguage();
  const { user, isUser, isAdmin } = useAuth();
  const [currency, setCurrency] = useState("mxn");
  const [modalityId, setModalityId] = useState(
    service?.modalities?.[0]?.id || null
  );
  const [copiedKey, setCopiedKey] = useState("");

  const loggedIn = isUser || isAdmin;
  const isMexican = useMemo(() => {
    if (!user) return null;
    if (isAdmin) return true;
    const c = (user.country || "").toLowerCase().trim();
    if (!c) return true;
    return ["méxico", "mexico", "mx"].some((tok) => c.includes(tok));
  }, [user, isAdmin]);

  if (!service) return null;
  const selectedModality =
    service.modalities?.find((m) => m.id === modalityId) || null;
  const priceMXN = selectedModality ? selectedModality.priceMXN : service.priceMXN;
  const priceUSD = selectedModality ? selectedModality.priceUSD : service.priceUSD;
  const price = currency === "mxn" ? priceMXN : priceUSD;
  const locked = !hasPrice(priceMXN);
  const cur = currency.toUpperCase();

  const handleCopy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success(t("dialog.copied"));
      setTimeout(() => setCopiedKey(""), 1500);
    } catch {
      toast.error("Error");
    }
  };

  const modalityLabel = selectedModality ? selectedModality.label[lang] : "";
  const waMsg = encodeURIComponent(
    lang === "es"
      ? `Hola, me interesa el servicio "${service.name.es}"${
          modalityLabel ? ` modalidad ${modalityLabel}` : ""
        }${locked ? "" : ` por $${price.toLocaleString()} ${cur}`}.`
      : `Hello, I'm interested in the service "${service.name.en}"${
          modalityLabel ? ` (${modalityLabel} modality)` : ""
        }${locked ? "" : ` for $${price.toLocaleString()} ${cur}`}.`
  );

  const bankRows = [
    { key: "bank", label: t("dialog.bank"), value: BANK_INFO.bank },
    { key: "clabe", label: t("dialog.clabe"), value: BANK_INFO.clabe },
    { key: "card", label: t("dialog.card"), value: BANK_INFO.card },
    { key: "beneficiary", label: t("dialog.beneficiary"), value: BANK_INFO.beneficiary },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`service-dialog-${service.id}`}
        className="max-w-2xl bg-[#0a0a0a] border border-[#d4af37]/30 text-neutral-100 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[#d4af37] mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.3em]">
              {service.category === "lectura"
                ? lang === "es"
                  ? "Lectura"
                  : "Reading"
                : lang === "es"
                ? "Hechicería"
                : "Witchcraft"}
            </span>
          </div>
          <DialogTitle className="font-serif text-3xl text-white">
            {service.name[lang]}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 italic">
            "{service.description[lang]}"
          </DialogDescription>
        </DialogHeader>

        {/* Modality selector (only for services with modalities) */}
        {service.modalities && service.modalities.length > 0 && (
          <div className="mt-2 mb-1">
            <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">
              {lang === "es" ? "Selecciona modalidad" : "Select modality"}
            </div>
            <div
              className={`grid gap-2 ${
                service.modalities.length === 2 ? "grid-cols-2" : "grid-cols-3"
              }`}
              data-testid="modality-selector"
            >
              {service.modalities.map((m) => {
                const active = modalityId === m.id;
                return (
                  <button
                    key={m.id}
                    data-testid={`modality-btn-${m.id}`}
                    onClick={() => setModalityId(m.id)}
                    className={`p-3 rounded-lg border transition-all text-left ${
                      active
                        ? "border-[#d4af37] bg-[#d4af37]/10"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400">
                      {m.label[lang]}
                    </div>
                    {hasPrice(m.priceMXN) ? (
                      <>
                        <div className="text-sm text-white font-serif mt-0.5">
                          ${m.priceMXN.toLocaleString("es-MX")}{" "}
                          <span className="text-[10px] text-neutral-500">MXN</span>
                        </div>
                        <div className="text-xs gold-text">
                          ${m.priceUSD.toLocaleString("en-US")}{" "}
                          <span className="text-[10px] text-neutral-500">USD</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-[10px] text-[#d4af37] mt-1 flex items-center gap-1"><Lock className="w-3 h-3" /> {lang === "es" ? "Precio al registrarte" : "Price after sign-up"}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Price selector */}
        {locked ? (
          <div className="my-4 rounded-lg border border-[#d4af37]/30 bg-[#d4af37]/5 p-4 text-center" data-testid="dialog-price-lock">
            <p className="text-sm text-neutral-300 mb-3">{lang === "es" ? "Los precios en MXN y USD y los métodos de pago se muestran al crear tu cuenta gratis." : "Prices in MXN and USD and payment methods are shown once you create your free account."}</p>
            <PriceLock lang={lang} testId="dialog-price-lock-btn" />
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-3 my-4">
          <button
            data-testid="currency-mxn"
            onClick={() => setCurrency("mxn")}
            className={`p-4 rounded-lg border transition-all text-left ${
              currency === "mxn"
                ? "border-[#d4af37] bg-[#d4af37]/10"
                : "border-white/10 hover:border-white/20"
            }`}
          >
            <div className="text-[10px] uppercase tracking-widest text-neutral-400">México (MXN)</div>
            <div className="font-serif text-2xl text-white">
              ${priceMXN.toLocaleString("es-MX")}
            </div>
          </button>
          <button
            data-testid="currency-usd"
            onClick={() => setCurrency("usd")}
            className={`p-4 rounded-lg border transition-all text-left ${
              currency === "usd"
                ? "border-[#d4af37] bg-[#d4af37]/10"
                : "border-white/10 hover:border-white/20"
            }`}
          >
            <div className="text-[10px] uppercase tracking-widest text-neutral-400">
              {lang === "es" ? "Internacional (USD)" : "International (USD)"}
            </div>
            <div className="font-serif text-2xl gold-text">
              ${priceUSD.toLocaleString("en-US")}
            </div>
          </button>
        </div>
        )}

        {/* Features */}
        <div className="rounded-lg border border-white/10 p-4 mb-4 bg-black/40">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] mb-3">
            {t("card.includes")}
          </div>
          <ul className="space-y-2">
            {service.features[lang].map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-neutral-200">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Bank details — gated behind auth + adapted to user country */}
        {!loggedIn ? (
          <div
            data-testid="dialog-auth-gate"
            className="rounded-lg border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#d4af37]/10 via-purple-900/10 to-black p-5 text-center"
          >
            <Lock className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
            <p className="text-sm text-neutral-300 mb-3">
              {lang === "es"
                ? "Inicia sesión o crea tu cuenta para ver los métodos de pago y datos bancarios."
                : "Log in or create your account to see payment methods and bank details."}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/login" onClick={() => onOpenChange?.(false)} className="btn-gold px-5 py-2 rounded-full text-xs uppercase tracking-widest">
                {lang === "es" ? "Iniciar sesión" : "Log in"}
              </Link>
              <Link to="/registro" onClick={() => onOpenChange?.(false)} className="btn-outline-gold px-5 py-2 rounded-full text-xs uppercase tracking-widest">
                {lang === "es" ? "Crear cuenta" : "Create account"}
              </Link>
            </div>
          </div>
        ) : isMexican ? (
          <div className="rounded-lg border border-[#d4af37]/30 bg-black/60" data-testid="dialog-mx-block">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
              <Banknote className="w-4 h-4 text-[#d4af37]" />
              <span className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">
                {t("dialog.bankData")}
              </span>
            </div>
            <div className="divide-y divide-white/5">
              {bankRows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between p-3"
                  data-testid={`bank-row-${row.key}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {row.label}
                    </div>
                    <div className="text-sm text-white font-mono truncate">{row.value}</div>
                  </div>
                  <button
                    data-testid={`btn-copy-${row.key}`}
                    onClick={() => handleCopy(row.key, row.value)}
                    className="text-xs uppercase tracking-widest text-[#d4af37] hover:text-white px-3 py-1.5 rounded-full border border-[#d4af37]/40 hover:bg-[#d4af37]/10 flex items-center gap-1.5 ml-2"
                  >
                    {copiedKey === row.key ? (
                      <>
                        <Check className="w-3 h-3" /> {t("dialog.copied")}
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" /> {t("dialog.copy")}
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="dialog-intl-block">
            {/* BBVA México with international beneficiary name */}
            <div className="rounded-lg border-2 border-[#d4af37]/40 bg-black/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Banknote className="w-4 h-4 text-[#d4af37]" />
                <span className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">
                  {lang === "es" ? "Transferencia BBVA México" : "BBVA Mexico Bank Transfer"}
                </span>
              </div>
              <div className="space-y-1.5 text-xs text-neutral-200">
                <div><span className="text-neutral-500 uppercase">Banco:</span> {BANK_INFO.internationalBank.bank}</div>
                <div><span className="text-neutral-500 uppercase">CLABE:</span> <span className="font-mono">{BANK_INFO.internationalBank.clabe}</span></div>
                <div><span className="text-neutral-500 uppercase">Tarjeta:</span> <span className="font-mono">{BANK_INFO.internationalBank.card}</span></div>
                <div><span className="text-neutral-500 uppercase">Beneficiario:</span> {BANK_INFO.internationalBank.beneficiary}</div>
              </div>
            </div>
            <div className="rounded-lg border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 via-purple-900/10 to-black p-4">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-sky-300" />
                <span className="text-xs uppercase tracking-widest text-sky-300 font-semibold">
                  {lang === "es" ? "Envío internacional" : "International transfer"}
                </span>
              </div>
              <p className="text-xs text-neutral-300 mb-2">
                {lang === "es"
                  ? "Usa Remitly, Wise, Xoom, Ria, Western Union o MoneyGram para enviar tu pago desde fuera de México."
                  : "Use Remitly, Wise, Xoom, Ria, Western Union or MoneyGram to send your payment from outside Mexico."}
              </p>
            </div>
            <div className="rounded-lg border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-black to-black p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-amber-300" />
                <span className="text-xs uppercase tracking-widest text-amber-300 font-semibold">
                  {lang === "es" ? "Retiro en ventanilla (Soriana)" : "In-person pickup (Soriana)"}
                </span>
              </div>
              <div className="space-y-1.5 text-xs text-neutral-200">
                <div><span className="text-neutral-500 uppercase">Nombre:</span> {BANK_INFO.internationalRemittance.pickupName}</div>
                <div><span className="text-neutral-500 uppercase">Estado:</span> {BANK_INFO.internationalRemittance.pickupState}</div>
                <div><span className="text-neutral-500 uppercase">Tel:</span> {BANK_INFO.internationalRemittance.pickupPhone}</div>
              </div>
            </div>
          </div>
        )}

        {/* PayPal — visible to ALL logged-in users (MX + INTL) */}
        {loggedIn && (
          <div
            data-testid="dialog-paypal-block"
            className="mt-3 rounded-lg border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 to-blue-500/5 p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="text-xl">💳</div>
                <span className="text-xs uppercase tracking-widest text-sky-300 font-semibold">
                  {lang === "es" ? "Pago por PayPal" : "Pay with PayPal"}
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-sky-300 bg-sky-500/15 border border-sky-500/40 rounded-full px-2 py-0.5">
                {lang === "es" ? "Internacional" : "International"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  {lang === "es" ? "Correo PayPal" : "PayPal Email"}
                </div>
                <div className="text-xs text-white font-mono truncate" data-testid="dialog-paypal-email">{BANK_INFO.paypalEmail}</div>
              </div>
              <button
                data-testid="btn-copy-paypal"
                onClick={() => handleCopy("paypal", BANK_INFO.paypalEmail)}
                className="text-sky-300 hover:text-white text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 hover:bg-sky-500/10"
              >
                {copiedKey === "paypal" ? <><Check className="w-3 h-3" /> {t("dialog.copied")}</> : <><Copy className="w-3 h-3" /> {t("dialog.copy")}</>}
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-neutral-400 italic mt-4 px-1">
          {t("dialog.afterPayment")}
        </p>

        <a
          data-testid="btn-contact-whatsapp"
          href={`${BANK_INFO.whatsappLink}?text=${waMsg}`}
          target="_blank"
          rel="noreferrer"
          className="w-full btn-gold mt-3 py-4 rounded-lg flex items-center justify-center gap-2 uppercase text-xs tracking-[0.2em]"
        >
          <MessageCircle className="w-4 h-4" /> {t("dialog.contactWhatsapp")}
        </a>
      </DialogContent>
    </Dialog>
  );
};
